import {goBack} from '@github-ui/history'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useNavigate} from '@github-ui/use-navigate'
import {Link} from '@primer/react'
import {type Dispatch, type SetStateAction, useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import type {RepositoryNode} from './components/milestone-form-query-types'
import {MilestoneForm} from './components/MilestoneForm'
import {LABELS} from './constants/labels'
import styles from './MilestoneCreateEdit.module.css'
import {MilestoneError} from './MilestoneError'
import type {CreateMilestoneInput} from './mutations/__generated__/createRepositoryMilestoneMutation.graphql'
import {commitCreateRepositoryMilestoneMutation} from './mutations/create-repository-milestone-mutation'
import {formatFormErrorMessage} from './utils/format-form-error-message'

// RELAY-CLEANUP(RepositoryMilestoneNewPageContentInternal): widens nullable fields so the still-Relay parent
// data is assignable. Remove when RepositoryMilestoneNewPageContentInternal leaves Relay.
export type MilestoneCreateFragment = RelayCompat<Pick<RepositoryNode, 'id' | 'nameWithOwner' | 'viewerCanPush'>>

type MilestoneCreateProps = {
  repository: MilestoneCreateFragment
}

export function MilestoneCreate({repository}: MilestoneCreateProps) {
  const navigate = useNavigate()
  const environment = useRelayEnvironment()

  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)

  const handleFormSubmit = useCallback(
    (input: CreateMilestoneInput, setIsSubmitting: Dispatch<SetStateAction<boolean>>) => {
      setSubmissionErrors(null)

      if (!repository?.viewerCanPush) {
        setSubmissionErrors(LABELS.milestoneCreatePermissionError)
        setIsSubmitting(false)
        return
      }

      commitCreateRepositoryMilestoneMutation({
        environment,
        input,
        onError: (error: Error) => {
          setIsSubmitting(false)
          if (error.cause && Array.isArray(error.cause)) {
            setSubmissionErrors(formatFormErrorMessage(error.cause))
          } else {
            setSubmissionErrors(LABELS.milestoneCreateError)
          }
        },
        onCompleted: response => {
          if (!response.createMilestone?.milestone || response?.createMilestone?.errors?.length > 0) {
            setSubmissionErrors(LABELS.milestoneCreateError)
            setIsSubmitting(false)
            return
          }
          navigate(`/${repository.nameWithOwner}/milestone/${response.createMilestone?.milestone.number}`)
        },
      })
    },
    [environment, navigate, repository],
  )

  const handleFormCancel = () => {
    goBack()
  }

  const formDescription = (
    <p className={styles.milestonePageDescription}>
      {LABELS.createMilestoneDescription} {LABELS.learnMorePrefix}{' '}
      <Link href="https://docs.github.com/en/issues/tracking-your-work-with-issues/about-issues" inline>
        {LABELS.milestonesAndIssues}
      </Link>
      .
    </p>
  )

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  return (
    <div
      className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}
      data-hpc
      data-testid="milestone-create"
    >
      <ErrorBoundary
        fallback={<MilestoneError title={LABELS.milestonePageError} message={LABELS.milestonePageErrorMessage} />}
      >
        <MilestoneForm
          repository={repository}
          formTitle={LABELS.createMilestone}
          formDescription={formDescription}
          formSubmitLabel={LABELS.createMilestone}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          submissionErrors={submissionErrors}
        />
      </ErrorBoundary>
    </div>
  )
}
